/**
 * Reset Password Page - Trade Loop v2.0
 *
 * Handles password reset after user clicks email link
 * Validates tokens from URL and allows setting a new password
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { supabase } from "@/lib/supabase";
import tradeLoopLogo from "@/assets/trade-loop-logo.svg";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [recoverySession, setRecoverySession] = useState<any>(null);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // Wait for Supabase to establish session from URL hash
  // Use onAuthStateChange to detect when session is ready
  useEffect(() => {
    let isMounted = true;
    let authListener: any;

    const initSession = async () => {
      // Parse hash parameters (Supabase uses this for auth callbacks)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hashAccessToken = hashParams.get('access_token');
      const hashType = hashParams.get('type');
      const hashError = hashParams.get('error');
      const hashErrorDescription = hashParams.get('error_description');

      console.log('Reset password - URL hash:', {
        fullHash: window.location.hash,
        accessToken: !!hashAccessToken,
        type: hashType,
        error: hashError,
        errorDescription: hashErrorDescription,
        allHashParams: Object.fromEntries(hashParams.entries())
      });

      // Check for errors in the hash
      if (hashError) {
        if (isMounted) {
          setHasError(true);
          showError(
            "Reset Link Error",
            hashErrorDescription || "This password reset link has expired or is invalid."
          );
        }
        return;
      }

      // Must have access token and recovery type
      if (!hashAccessToken || hashType !== 'recovery') {
        if (isMounted) {
          setHasError(true);
          showError(
            "Invalid Reset Link",
            "This password reset link is invalid or expired."
          );
        }
        return;
      }

      // Set up auth state listener BEFORE calling setSession
      // This ensures we catch the PASSWORD_RECOVERY event if it fires
      authListener = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state change:', { event, hasSession: !!session });

        // PASSWORD_RECOVERY event indicates session is ready
        if (event === 'PASSWORD_RECOVERY' && session && isMounted) {
          console.log('Password recovery session established via PASSWORD_RECOVERY event');
          setSessionReady(true);
        }
      });

      // Manually set the session using the tokens from the URL hash
      try {
        console.log('Manually setting session from URL hash tokens...');
        const { data, error } = await supabase.auth.setSession({
          access_token: hashAccessToken,
          refresh_token: hashParams.get('refresh_token') || ''
        });

        console.log('setSession result:', {
          hasSession: !!data.session,
          hasUser: !!data.user,
          userId: data.user?.id,
          error: error?.message
        });

        if (error) {
          throw error;
        }

        if (!data.session) {
          throw new Error('setSession did not return a session');
        }

        // Store the session from setSession() - this is the valid session
        // Don't rely on getSession() which may not have persisted the session yet
        console.log('Session established from setSession response, storing for password update');
        setRecoverySession(data.session);

        // Wait a moment for the PASSWORD_RECOVERY event to fire
        // If it doesn't fire within 500ms, enable form anyway since we have a valid session
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check if PASSWORD_RECOVERY event already set sessionReady
        if (isMounted && !sessionReady) {
          console.log('PASSWORD_RECOVERY event did not fire, but setSession returned valid session - enabling form');
          setSessionReady(true);
        }
      } catch (error: any) {
        console.error('Failed to set session:', error);
        if (isMounted) {
          setHasError(true);
          showError(
            "Session Error",
            error.message || "Unable to establish password reset session."
          );
        }
      }
    };

    initSession();

    // Cleanup
    return () => {
      isMounted = false;
      if (authListener?.data?.subscription) {
        authListener.data.subscription.unsubscribe();
      }
    };
  }, [showError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      showError("Missing Fields", "Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      showError("Passwords Don't Match", "Please ensure both passwords match");
      return;
    }

    if (password.length < 8) {
      showError("Weak Password", "Password must be at least 8 characters");
      return;
    }

    setIsLoading(true);

    try {
      // Verify we have a recovery session
      if (!recoverySession) {
        throw new Error('Session expired. Please refresh the page and try again.');
      }

      console.log('Updating password with recovery session:', {
        hasSession: !!recoverySession,
        userId: recoverySession?.user?.id,
        accessToken: !!recoverySession.access_token
      });

      // Call the backend API endpoint which uses the admin client to bypass RLS
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/auth/reset-password-with-token`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessToken: recoverySession.access_token,
          newPassword: password
        })
      });

      console.log('Password update response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Password update failed:', errorData);
        throw new Error(errorData.error || 'Failed to update password');
      }

      const result = await response.json();
      console.log('Password updated successfully:', result);

      // Clear the URL hash for security
      window.history.replaceState(null, '', window.location.pathname);

      // Show success message
      showSuccess(
        "Password Updated!",
        "Your password has been successfully reset"
      );

      // Redirect to login immediately (toast will show on login page)
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('Password reset error:', error);
      showError(
        "Failed to reset password",
        error.message || "Please try requesting a new reset link"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (hasError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white">
        <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
          <div className="flex items-center justify-center max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
            <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          </div>
        </header>

        <main className="px-4 py-8">
          <div className="max-w-sm mx-auto">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl">
              <CardContent className="px-6 py-10 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                  Invalid Reset Link
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  This password reset link is invalid or has expired.
                </p>
                <Link to="/forgot-password">
                  <Button className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]">
                    Request New Reset Link
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white">
        <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
          <div className="flex items-center justify-center max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
            <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          </div>
        </header>

        <main className="px-4 py-8">
          <div className="max-w-sm mx-auto">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl animate-in slide-in-from-bottom-4 duration-500">
              <CardContent className="px-6 py-10 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-300 delay-150">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                  Password Reset!
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Your password has been successfully updated. You can now sign in with your new password.
                </p>
                <Link to="/login">
                  <Button className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]">
                    Go to Sign In
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white">
      {/* Header */}
      <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
        <div className="flex items-center justify-between max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <Link to="/login">
            <Button variant="ghost" size="sm" className="p-3 hover:bg-white/60 rounded-xl backdrop-blur-sm border border-white/20 transition-all duration-200">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </Button>
          </Link>
          <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          <div className="w-11"></div>
        </div>
      </header>

      {/* Reset Password Form */}
      <main className="px-4 py-8">
        <div className="max-w-sm mx-auto">
          <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl animate-in slide-in-from-bottom-4 duration-500">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
                Set New Password
              </CardTitle>
              <p className="text-gray-600 text-lg leading-relaxed">
                {!sessionReady ? "Verifying reset link..." : "Enter your new password below"}
              </p>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {!sessionReady && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
              {sessionReady && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="h-12 bg-white/80 backdrop-blur-sm border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200 pr-12"
                      required
                      disabled={!sessionReady || isLoading}
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Must be at least 8 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-700">
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="h-12 bg-white/80 backdrop-blur-sm border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200 pr-12"
                      required
                      disabled={!sessionReady || isLoading}
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={!sessionReady || !password || !confirmPassword || password !== confirmPassword || isLoading}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:shadow-none"
                >
                  {isLoading ? "Updating Password..." : "Reset Password"}
                </Button>

                <div className="text-center pt-2">
                  <Link to="/login">
                    <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200">
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
